import { useEffect, useState } from 'react'

import { Box, CircularProgress, Typography } from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { ConfirmationDialog } from '@scoir/modals'
import { Link } from 'react-router-dom'

import api from 'common/api'
import { ApiRecord } from 'routes/highschools/ManageVisits/types'
import { User } from 'store/user'

type DeleteUserDialogProps = {
  open: boolean
  user?: User
  onClose: () => void
  onConfirm: () => void
}

const DeleteUserDialog = ({ open, user, onClose, onConfirm }: DeleteUserDialogProps) => {
  const [visits, setVisits] = useState<ApiRecord[]>([])
  const [loadingVisits, setLoadingVisits] = useState(false)
  const ROOT_PATH = import.meta.env.BASE_URL
  const isAdmissionsRepOrVisitsManager =
    user?.Roles?.includes('AdmissionsRepresentative') || user?.Roles?.includes('VisitsManager')

  // When the dialog opens for a staff member, check whether they have any scheduled
  // visits so we can surface them before the delete is confirmed.
  useEffect(() => {
    if (!open || !user) return

    // Skip the API call if the user is not an Admissions Rep or Visits Manager, since they won't have any visits assigned to them.
    if (!isAdmissionsRepOrVisitsManager) {
      setVisits([])
      setLoadingVisits(false)
      return
    }

    setVisits([])
    setLoadingVisits(true)
    api
      .get<ApiRecord[]>('/api/college/visits')
      .then((response) => {
        setVisits((response ?? []).filter((visit) => visit.rep.userId === user.Id))
      })
      .catch(() => {
        setVisits([])
      })
      .finally(() => {
        setLoadingVisits(false)
      })
  }, [open, user])

  return (
    <ConfirmationDialog
      open={open}
      headerText="Delete Staff Member"
      onClose={onClose}
      onConfirm={onConfirm}
    >
      <Typography>
        Are you sure you want to delete{' '}
        <strong>{user ? user.FirstName + ' ' + user.LastName : 'this staff member'}</strong>?
      </Typography>
      {isAdmissionsRepOrVisitsManager ? (
        <Box mt={2}>
          {loadingVisits ? (
            <Box
              display="flex"
              alignItems="center"
            >
              <CircularProgress
                size={16}
                style={{ marginRight: 8 }}
              />
              <Typography variant="caption">Checking for scheduled visits...</Typography>
            </Box>
          ) : visits.length > 0 ? (
            <Alert severity="error">
              This staff member has {visits.length} upcoming high school {visits.length === 1 ? 'visit' : 'visits'}{' '}
              scheduled. Deleting this user will leave these visits without an assigned rep. Please reassign or cancel
              them first. You can do this from the <Link to={`${ROOT_PATH}/recruit/manage-visits`}>Manage Visits</Link>{' '}
              page.
            </Alert>
          ) : (
            <Typography variant="caption">This user has no scheduled visits and can be safely deleted.</Typography>
          )}
        </Box>
      ) : null}
    </ConfirmationDialog>
  )
}

export default DeleteUserDialog
